// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Interactive results table shared by the AI Report Builder and the pharmacy Reports page.
 *
 *  • Client mode (default): the whole result is in `rows` — search, per-column filters, group-by,
 *    date range, sort and pagination all run in the browser across the loaded set.
 *  • Controlled server mode (`server` given): `rows` is ONE page from the server. The controls STAGE a
 *    query; Apply (and sort / group / paging / page-size) call `server.onQuery`, and the parent
 *    re-fetches — so filter/search/sort/group run over the WHOLE dataset server-side while the browser
 *    only ever holds one page. Filters/search/date are applied on Apply (one query, not per keystroke).
 *
 * Columns resize (shared hook), cells show their full value on hover, Export pulls the COMPLETE set.
 * No raw dropdowns except SearchableSelect (Gate 19).
 */
import { useEffect, useMemo, useState } from 'react'
import { ArrowUp, ArrowDown, Search, Download, ChevronLeft, ChevronRight, SlidersHorizontal, Layers, Play } from 'lucide-react'
import SearchableSelect from './SearchableSelect'
import { useColumnWidths, ColumnResizeHandle } from './ColumnResize'
import { isIdentifierColumn } from '../../api/format'
import ColumnValueFilter from './ColumnValueFilter'

const PAGE_SIZES = [10, 20, 50, 100]

export interface ReportServerQuery {
  page: number; pageSize: number
  search: string
  // Each filter is EITHER free-text `val` (contains) OR exact `vals` (checkbox value picker -> IN).
  filters: { col: string; val?: string; vals?: string[] }[]
  dateCol: string | null; from: string; to: string
  sortCol: string | null; sortDesc: boolean
  groupBy: string | null
  // Ask the server for the total record count (only on filter/group/apply changes, not page/sort).
  wantCount?: boolean
}

export interface ReportResultTableProps {
  columns: string[]
  rows: unknown[][]
  summary?: React.ReactNode
  onExport?: () => void
  exporting?: boolean
  // Given the value of a `pod` column (a shipment id), return the href that opens its Proof of
  // Delivery. When provided, cells in a column named `pod` / `*_pod` render as a "POD" link.
  podHref?: (shipmentId: string) => string
  server?: {
    page: number
    pageSize: number
    hasMore: boolean
    busy?: boolean
    baseColumns: string[]       // original (ungrouped) columns — drive the pickers + search
    totalCount?: number | null  // total records across the whole filtered dataset (server-computed)
    onQuery: (q: ReportServerQuery) => void
    // Distinct values of a column across the whole report (optionally filtered by `search`) -> the
    // searchable checkbox value picker.
    onDistinct?: (col: string, search?: string) => Promise<{ values: string[]; too_many: boolean }>
  }
}

export default function ReportResultTable({ columns, rows, summary, onExport, exporting, server, podHref }: ReportResultTableProps) {
  const [query, setQuery] = useState('')
  const [colFilters, setColFilters] = useState<Record<string, string>>({})
  const [colValues, setColValues] = useState<Record<string, string[]>>({})  // checkbox value-picker selections
  // D-UI-2: every column's filter is on screen from the start. It used to sit behind a toolbar
  // button, so a report opened looking as though its columns could not be filtered.
  const [showColFilters, setShowColFilters] = useState(true)
  const [sortCol, setSortCol] = useState<number | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [groupBy, setGroupBy] = useState<number | null>(null)   // client-mode index; server-mode uses names
  const [srvGroupBy, setSrvGroupBy] = useState('')              // server-mode group-by column NAME
  const [clientPage, setClientPage] = useState(0)
  const [clientPageSize, setClientPageSize] = useState(50)

  const pickerCols = server ? server.baseColumns : columns

  // Generic, reusable date range: any date-looking column is selectable, and the from/to applies to
  // whichever one is picked (server-side via dateCol/from/to; client-side via dateColIdx).
  const dateCols = useMemo(
    () => pickerCols.filter(c => /(_at$|_date$|_pst$|_on$|shipped|delivered|created|updated|approved|\bdate\b)/i.test(c)),
    [pickerCols])
  const [dateCol, setDateCol] = useState('')
  useEffect(() => { setDateCol(prev => (dateCols.includes(prev) ? prev : (dateCols[0] ?? ''))) }, [dateCols])
  const dateColName = dateCol || null
  const dateColIdx = dateCol ? columns.indexOf(dateCol) : -1

  const numericCols = useMemo(() => {
    const set = new Set<number>()
    for (let i = 0; i < columns.length; i++) {
      let seen = 0, num = 0
      for (const r of rows) {
        const v = r[i]; if (v == null || String(v).trim() === '') continue
        seen++; if (Number.isFinite(Number(v))) num++
        if (seen >= 25) break
      }
      if (seen > 0 && num === seen) set.add(i)
    }
    return set
  }, [columns, rows])

  // ── Server (controlled) mode ────────────────────────────────────────────────────────────────────
  // A column filter is EITHER a checkbox value set (exact IN) OR free text (contains); value set wins.
  function buildFilters(): { col: string; val?: string; vals?: string[] }[] {
    const out: { col: string; val?: string; vals?: string[] }[] = []
    for (const cn of (server ? server.baseColumns : columns)) {
      const vals = colValues[cn]
      if (vals && vals.length) out.push({ col: cn, vals })
      else { const t = (colFilters[cn] ?? '').trim(); if (t) out.push({ col: cn, val: t }) }
    }
    return out
  }

  function emit(overrides: Partial<ReportServerQuery>) {
    if (!server) return
    server.onQuery({
      page: server.page, pageSize: server.pageSize,
      search: query.trim(),
      filters: buildFilters(),
      dateCol: dateColName, from, to,
      sortCol: sortCol != null ? columns[sortCol] : null, sortDesc: sortDir === 'desc',
      groupBy: srvGroupBy || null,
      wantCount: false,
      ...overrides,
    })
  }

  // ── Client-mode filtering / grouping / paging ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (server) return rows
    const q = query.trim().toLowerCase()
    const active = Object.entries(colFilters).filter(([, v]) => v.trim() !== '')
    const fromT = from ? Date.parse(from) : null
    const toT = to ? Date.parse(to) + 86_400_000 - 1 : null
    let out = rows
    if (q) out = out.filter(r => r.some(v => v != null && String(v).toLowerCase().includes(q)))
    if (active.length) out = out.filter(r => active.every(([name, v]) => {
      const i = columns.indexOf(name); return i >= 0 && String(r[i] ?? '').toLowerCase().includes(v.trim().toLowerCase())
    }))
    const activeVals = Object.entries(colValues).filter(([, v]) => v.length > 0)
    if (activeVals.length) out = out.filter(r => activeVals.every(([name, vals]) => {
      const i = columns.indexOf(name); return i < 0 || vals.includes(String(r[i] ?? ''))
    }))
    if (dateColIdx >= 0 && (fromT != null || toT != null)) {
      out = out.filter(r => { const t = Date.parse(String(r[dateColIdx] ?? '')); if (Number.isNaN(t)) return false; if (fromT != null && t < fromT) return false; if (toT != null && t > toT) return false; return true })
    }
    return out
  }, [server, rows, query, colFilters, colValues, from, to, dateColIdx, columns])

  const grouped = useMemo(() => {
    if (server || groupBy == null) return null
    // Smart aggregation, mirroring the server: numeric MEASURES are summed; numeric IDENTIFIERS
    // (rx_number, *_id, …) are COUNT(DISTINCT)ed, never summed; every group gets a row count.
    const cand = [...numericCols].filter(ci => ci !== groupBy).sort((a, b) => a - b)
    const sumCols = cand.filter(ci => !isIdentifierColumn(columns[ci]))
    const idCols = cand.filter(ci => isIdentifierColumn(columns[ci]))
    const map = new Map<string, { count: number; sums: number[]; dis: Set<string>[] }>()
    for (const r of filtered) {
      const key = fmt(r[groupBy]); let g = map.get(key)
      if (!g) { g = { count: 0, sums: sumCols.map(() => 0), dis: idCols.map(() => new Set<string>()) }; map.set(key, g) }
      g.count++
      sumCols.forEach((ci, k) => { const n = Number(r[ci]); if (Number.isFinite(n)) g!.sums[k] += n })
      idCols.forEach((ci, k) => { const v = r[ci]; if (v != null && String(v).trim() !== '') g!.dis[k].add(String(v)) })
    }
    return {
      cols: [columns[groupBy], 'count', ...sumCols.map(ci => `${columns[ci]} (sum)`), ...idCols.map(ci => `${columns[ci]} (distinct)`)],
      rows: [...map.entries()].map(([k, g]) => [k, g.count, ...g.sums.map(s => Math.round(s * 100) / 100), ...g.dis.map(d => d.size)]) as unknown[][],
    }
  }, [server, groupBy, filtered, numericCols, columns])

  const displayColumns = grouped ? grouped.cols : columns
  const resize = useColumnWidths(null, displayColumns)
  const displayRowsRaw = grouped ? grouped.rows : filtered
  const displayRows = useMemo(() => {
    if (server || sortCol == null || sortCol >= displayColumns.length) return displayRowsRaw
    const dir = sortDir === 'asc' ? 1 : -1
    return [...displayRowsRaw].sort((a, b) => cmp(a[sortCol], b[sortCol]) * dir)
  }, [server, displayRowsRaw, sortCol, sortDir, displayColumns.length])

  const pageCount = Math.max(1, Math.ceil(displayRows.length / clientPageSize))
  const safePage = Math.min(clientPage, pageCount - 1)
  const pageStart = safePage * clientPageSize
  const pageRows = server ? displayRows : displayRows.slice(pageStart, pageStart + clientPageSize)
  const isFiltered = !server && filtered.length !== rows.length
  const activePageSize = server ? server.pageSize : clientPageSize

  function toggleSort(i: number) {
    const dir = sortCol === i && sortDir === 'asc' ? 'desc' : 'asc'
    setSortCol(i); setSortDir(dir); setClientPage(0)
    if (server) server.onQuery({ page: 0, pageSize: server.pageSize, search: query.trim(), filters: buildFilters(), dateCol: dateColName, from, to, sortCol: columns[i] ?? null, sortDesc: dir === 'desc', groupBy: srvGroupBy || null })
  }

  // Cell rendering. A `pod` / `*_pod` column whose value is a shipment id renders as a "POD" link into
  // the shipment drawer (courier photos + POD PDF); a `*_url` / `*_link` column with a URL value renders
  // as a plain link. Everything else is plain text.
  function renderCell(col: string, v: unknown): React.ReactNode {
    const s = fmt(v)
    const raw = v == null ? '' : String(v).trim()
    if (raw === '') return s
    const lc = (col || '').toLowerCase()
    if (lc === 'pod' || lc.endsWith('_pod') || lc === 'pod_link') {
      // The server mints a signed pod-doc URL for the `pod` column; if signing is off it stays a bare
      // shipment id, so fall back to the shipment deep-link builder when one is provided.
      const href = /^(https?:\/\/|\/)/.test(raw) ? raw : (podHref ? podHref(raw) : null)
      return href ? <a href={href} target="_blank" rel="noopener noreferrer" style={cellLink}>POD ↗</a> : s
    }
    if ((lc.endsWith('_url') || lc.endsWith('_link')) && /^(https?:\/\/|\/)/.test(raw)) {
      return <a href={raw} target="_blank" rel="noopener noreferrer" style={cellLink}>open ↗</a>
    }
    return s
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap', fontSize: 13 }}>
        {summary}
        <div style={{ position: 'relative', marginLeft: summary ? 'auto' : 0 }}>
          <Search size={13} aria-hidden="true" style={{ position: 'absolute', left: 8, top: 8, color: 'var(--color-text-mute)' }} />
          <input value={query} onChange={e => { setQuery(e.target.value); setClientPage(0) }}
            onKeyDown={e => { if (server && e.key === 'Enter') emit({ page: 0, wantCount: true }) }}
            placeholder="Search…" aria-label="Search rows"
            style={{ padding: '6px 10px 6px 26px', border: '1px solid var(--color-border)', borderRadius: 7, font: 'inherit', fontSize: 12.5, background: 'var(--color-surface)', width: 160 }} />
        </div>
        <button onClick={() => setShowColFilters(s => !s)} style={{ ...toolBtn, ...(showColFilters ? sizeBtnActive : {}) }} title="Per-column filters">
          <SlidersHorizontal size={13} aria-hidden="true" /> Filters
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
          <Layers size={13} aria-hidden="true" style={{ color: 'var(--color-text-mute)' }} />
          <div style={{ width: 170 }}>
            {server ? (
              <SearchableSelect value={srvGroupBy} onChange={v => { setSrvGroupBy(v); emit({ groupBy: v || null, page: 0, wantCount: true }) }} ariaLabel="Group by"
                placeholder="Group by…" options={[{ value: '', label: 'No grouping' }, ...pickerCols.map(c => ({ value: c, label: c }))]} />
            ) : (
              <SearchableSelect value={groupBy == null ? '' : String(groupBy)} onChange={v => { setGroupBy(v === '' ? null : Number(v)); setSortCol(null); setClientPage(0) }} ariaLabel="Group by"
                placeholder="Group by…" options={[{ value: '', label: 'No grouping' }, ...columns.map((c, i) => ({ value: String(i), label: c }))]} />
            )}
          </div>
        </div>
        {dateCols.length > 0 && !grouped && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-mute)' }}>
            <div style={{ width: 150 }}>
              <SearchableSelect value={dateCol} onChange={v => { setDateCol(v); setClientPage(0) }} ariaLabel="Date field"
                placeholder="Date field…" options={dateCols.map(c => ({ value: c, label: c }))} />
            </div>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setClientPage(0) }} aria-label="From date" style={dateInput} />
            <span>→</span>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setClientPage(0) }} aria-label="To date" style={dateInput} />
            {(from || to) && <button onClick={() => { setFrom(''); setTo(''); if (server) emit({ from: '', to: '', page: 0, wantCount: true }) }} style={clearBtn}>clear</button>}
          </div>
        )}
        {server && (
          <button onClick={() => emit({ page: 0, wantCount: true })} disabled={server.busy} style={{ ...toolBtn, background: 'var(--color-primary, #0d9488)', color: '#fff', borderColor: 'var(--color-primary, #0d9488)' }}>
            <Play size={13} aria-hidden="true" /> Apply
          </button>
        )}
        {onExport && (
          <button onClick={onExport} disabled={exporting} style={exportBtn}>
            <Download size={13} aria-hidden="true" /> {exporting ? 'Exporting…' : 'Export CSV (all rows)'}
          </button>
        )}
      </div>

      {/* Pagination + page size — top */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
        {server ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => emit({ page: server.page - 1 })} disabled={server.page <= 0 || server.busy} style={pageBtn}><ChevronLeft size={13} aria-hidden="true" /> Prev</button>
            <span style={{ color: 'var(--color-text-mute)' }}>Page {server.page + 1}{server.totalCount != null ? ` of ${Math.max(1, Math.ceil(server.totalCount / server.pageSize)).toLocaleString()}` : ''}{server.busy ? ' · loading…' : ''}</span>
            <button onClick={() => emit({ page: server.page + 1 })} disabled={!server.hasMore || server.busy} style={pageBtn}>Next <ChevronRight size={13} aria-hidden="true" /></button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setClientPage(p => Math.max(0, p - 1))} disabled={safePage === 0} style={pageBtn}><ChevronLeft size={13} aria-hidden="true" /> Prev</button>
            <span style={{ color: 'var(--color-text-mute)' }}>{displayRows.length === 0 ? '0' : `${(pageStart + 1).toLocaleString()}–${Math.min(pageStart + clientPageSize, displayRows.length).toLocaleString()}`} of {displayRows.length.toLocaleString()}{grouped ? ' groups' : ''}</span>
            <button onClick={() => setClientPage(p => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1} style={pageBtn}>Next <ChevronRight size={13} aria-hidden="true" /></button>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-text-mute)' }}>
          <span>Per page:</span>
          {PAGE_SIZES.map(n => (
            <button key={n} onClick={() => { if (server) emit({ pageSize: n, page: 0 }); else { setClientPageSize(n); setClientPage(0) } }} style={{ ...sizeBtn, ...(activePageSize === n ? sizeBtnActive : {}) }}>{n}</button>
          ))}
        </div>
        {server && <span style={{ color: 'var(--color-text-mute)' }}>server-side · filters apply across the whole report on Apply</span>}
        {isFiltered && !grouped && <span style={{ color: 'var(--color-text-mute)' }}>(filtered from {rows.length.toLocaleString()}{onExport ? '; Export CSV = complete set' : ''})</span>}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
        <table data-funnels="own" ref={resize.tableRef} className={resize.tableClass}
          style={{ borderCollapse: 'collapse', fontSize: 12.5, tableLayout: resize.tableLayout, width: resize.totalWidth ?? '100%', minWidth: resize.totalWidth }}>
          <colgroup>{displayColumns.map((c, i) => <col key={i} style={{ width: resize.widthFor(c) }} />)}</colgroup>
          <thead>
            <tr style={{ background: 'var(--color-surface-alt, #f6f8fa)' }}>
              {displayColumns.map((c, i) => (
                <th key={c + i} onClick={() => toggleSort(i)} style={cellHead} title={c}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    {c}
                    {(server ? sortCol === i : sortCol === i) && (sortDir === 'asc' ? <ArrowUp size={11} aria-hidden="true" /> : <ArrowDown size={11} aria-hidden="true" />)}
                  </span>
                  <ColumnResizeHandle colKey={c} colIndex={i} api={resize} label={c} />
                </th>
              ))}
            </tr>
            {showColFilters && !grouped && (
              <tr style={{ background: 'var(--color-surface)' }}>
                {(server ? server.baseColumns : columns).map((cn, i) => (
                  <th key={i} style={{ padding: '4px 6px', borderBottom: '1px solid var(--color-border)', overflow: 'visible' }}>
                    <ColumnValueFilter
                      col={cn}
                      text={colFilters[cn] ?? ''}
                      values={colValues[cn] ?? []}
                      onText={v => { setColFilters(f => ({ ...f, [cn]: v })); setColValues(f => ({ ...f, [cn]: [] })); setClientPage(0) }}
                      onValues={vals => { setColValues(f => ({ ...f, [cn]: vals })); setColFilters(f => ({ ...f, [cn]: '' })); setClientPage(0) }}
                      onEnter={() => { if (server) emit({ page: 0, wantCount: true }) }}
                      fetchDistinct={server?.onDistinct}
                    />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {pageRows.map((row, ri) => (
              <tr key={pageStart + ri} style={{ borderTop: '1px solid var(--color-border)' }}>
                {row.map((v, ci) => <td key={ci} style={cell} title={fmt(v)}>{renderCell(displayColumns[ci], v)}</td>)}
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={displayColumns.length} style={{ ...cell, textAlign: 'center', color: 'var(--color-text-mute)', padding: '14px' }}>No rows.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}
function cmp(a: unknown, b: unknown): number {
  const an = a == null, bn = b == null
  if (an && bn) return 0
  if (an) return 1
  if (bn) return -1
  const na = Number(a), nb = Number(b)
  if (Number.isFinite(na) && Number.isFinite(nb) && String(a).trim() !== '' && String(b).trim() !== '') return na - nb
  const da = Date.parse(String(a)), db = Date.parse(String(b))
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db
  return String(a).localeCompare(String(b))
}

const cellHead: React.CSSProperties = { position: 'relative', textAlign: 'left', padding: '7px 10px', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '2px solid var(--color-border)', cursor: 'pointer', userSelect: 'none' }
const cell: React.CSSProperties = { padding: '6px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const cellLink: React.CSSProperties = { color: 'var(--color-primary, #0d9488)', fontWeight: 600, textDecoration: 'none' }
const dateInput: React.CSSProperties = { padding: '4px 6px', border: '1px solid var(--color-border)', borderRadius: 6, font: 'inherit', fontSize: 12, background: 'var(--color-surface)' }
const clearBtn: React.CSSProperties = { border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 11.5, color: 'var(--color-primary, #0d9488)', fontWeight: 600 }
const exportBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 7, background: 'var(--color-surface)', color: 'var(--color-text)', font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }
const toolBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 7, background: 'var(--color-surface)', color: 'var(--color-text)', font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
const pageBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '5px 10px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
const sizeBtn: React.CSSProperties = { padding: '3px 8px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', font: 'inherit', fontSize: 12, cursor: 'pointer' }
const sizeBtnActive: React.CSSProperties = { background: 'var(--color-primary, #0d9488)', color: '#fff', borderColor: 'var(--color-primary, #0d9488)' }
